import { normalizeApplicationDates, toDateInput } from '@shared/consumerPlan';
import { jsonParseBetter } from '@shared/jsonParseBetter';
import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';

export const consumerPlanKeys = createQueryKeys('consumerPlan');
export const consumerQuoteKeys = createQueryKeys('consumerQuotes');
export const consumerApplicationRecordKeys = createQueryKeys('consumerApplicationRecords');
export const consumerCarePlanSaveKey = (clientId: string) => ['consumerCarePlan', clientId, 'save'] as const;

export interface PolicyReviewCheckoutRequest {
  reviewType: ConsumerPolicyReviewType;
  documents: ConsumerPolicyReviewDocument[];
  responseDeadline: string | null;
  successPath: string;
  cancelPath: string;
}

export interface PremiumRangeRequest {
  paymentSchedule: ConsumerPaymentSchedule;
  minPremium: number;
  maxPremium: number;
  email: string;
}

function normalizePlanResponse(response: ConsumerPlanResponse): ConsumerPlanResponse {
  const { application, partner, completedAt } = response.state;
  return {
    ...response,
    state: {
      ...response.state,
      application: application && normalizeApplicationDates(application),
      partner: partner && { ...partner, birthDate: toDateInput(partner.birthDate) },
      // The API client may hand back ISO strings as Dates; the plan keeps the string.
      completedAt: completedAt == null ? null : new Date(completedAt).toISOString(),
    },
  };
}

// The client container goes straight into Redux, whose reducer runs the engine and needs real Dates
// (birth dates, survey dates). This API client leaves ISO strings, so the container is parsed the
// way the Redux API middleware parses every response it handles.
function reviveClientContainer(container: ClientContainer): ClientContainer {
  return jsonParseBetter<ClientContainer>(JSON.stringify(container)) ?? container;
}

async function withNormalizedResponse(request: Promise<ConsumerAddedQuoteResponse>) {
  const response = await request;
  return {
    ...response,
    clientContainer: reviveClientContainer(response.clientContainer),
    plan: normalizePlanResponse(response.plan),
  };
}

export const consumerApi = {
  getPlan: async (clientId: string): Promise<ConsumerPlanResponse> =>
    normalizePlanResponse(await WaterlilyClient.get<ConsumerPlanResponse>(`/client/${clientId}/consumer-plan`)),

  /** The consumer's sent applications exactly as sent, with their answers and consents. */
  getApplicationRecords: (clientId: string) =>
    WaterlilyClient.get<ConsumerApplicationRecord[]>(`/client/${clientId}/consumer-applications`),

  /** Selects a quote: the server saves it as the consumer's one added quote and records the selection. */
  putAddedQuote: (clientId: string, quoteKey: string) =>
    withNormalizedResponse(
      WaterlilyClient.put<ConsumerAddedQuoteResponse>(`/client/${clientId}/consumer-added-quote`, { quoteKey }),
    ),

  /** Removes the added quote from the plan; an application already sent for it is kept as sent. */
  deleteAddedQuote: (clientId: string) =>
    withNormalizedResponse(WaterlilyClient.delete<ConsumerAddedQuoteResponse>(`/client/${clientId}/consumer-added-quote`)),

  savePlan: (clientId: string, state: ConsumerPlanState) =>
    WaterlilyClient.put<ConsumerPlanState>(`/client/${clientId}/consumer-plan`, { state }),

  getQuotes: (clientId: string) =>
    WaterlilyClient.get<ConsumerQuotesResponse>(`/client/${clientId}/consumer-quotes`),

  submitApplication: (
    clientId: string,
    body: { coverage: ConsumerApplicationCoverage; answers: ConsumerApplicationAnswers; consentIds: string[] },
  ) =>
    WaterlilyClient.post<ConsumerApplicationSubmission>(
      `/client/${clientId}/consumer-application`,
      body,
    ),

  getPolicyReviewUploadUrl: (clientId: string, body: { fileName: string; contentType: string }) =>
    WaterlilyClient.post<ConsumerPolicyReviewDocumentUpload>(
      `/client/${clientId}/consumer-policy-review-document-url`,
      body,
    ),

  startPolicyReviewCheckout: (clientId: string, body: PolicyReviewCheckoutRequest) =>
    WaterlilyClient.post<ConsumerPolicyReviewCheckout>(
      `/client/${clientId}/consumer-policy-review`,
      body,
    ),

  requestPremiumRange: (clientId: string, body: PremiumRangeRequest) =>
    WaterlilyClient.post<{ requested: true }>(`/client/${clientId}/consumer-quote-request`, body),

  saveCareEnvironmentSelections: (clientId: string, careEnvironmentSelections: CareEnvironmentSelections) =>
    WaterlilyClient.put<unknown>(`/client/${clientId}/care-environment-selections`, { clientId, careEnvironmentSelections }),

  saveCustomInferences: (clientId: string, clientCustomInferences: PutClientCustomInferencesBodyProps['clientCustomInferences']) =>
    WaterlilyClient.put<unknown>(`/client/${clientId}/client-custom-inferences`, { clientId, clientCustomInferences }),

  getPolicyReviewDocumentDownloadUrl: (reviewId: string, documentIndex: number) =>
    WaterlilyClient.post<{ url: string; fileName: string }>('/consumer-policy-review-document-download-url', {
      reviewId,
      documentIndex,
    }),

  resetFlowForWizard: (clientId: string) =>
    WaterlilyClient.delete<{ clientId: string }>(`/client/${clientId}/consumer-flow`),
};
